/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Collapse, Grid, IconButton, Typography } from '@mui/material';
import {
	Add as AddIcon,
	Build as OperationsIcon,
	CreditCard as CreditCardIcon,
	FlightTakeoff as EmissionsIcon,
	MoneyOff as RefundsIcon,
} from '@mui/icons-material';
import { IOperationalRequestResponse, IPassenger, IServices, ITransaction } from 'features/salesOrder/types';
import { OperationsServiceRequestModal } from './OperationsServiceRequestModal';
import { EmissionsRequestModal } from './EmissionsRequestModal';
import { RefundRequestModal } from './RefundRequestModal';
import { CreditCardInquiryModal } from './CreditCardInquiryModal';
import { OperationalRequestsList } from './OperationalRequestsList';
import { ShowAlertState } from 'features/common/types';
import { getOperationalRequests } from 'features/salesOrder/services';

interface OperationalRequestsProps {
	services: IServices[];
	transactions: ITransaction[];
	passengers: IPassenger[];
	saleOrderId: string;
	onSetAlert: (alert: ShowAlertState) => void;
	onRequestCreated?: () => void; // Callback para refrescar la lista cuando se crea una solicitud
}

export const OperationalRequests: React.FC<OperationalRequestsProps> = ({
	services,
	transactions,
	passengers,
	saleOrderId,
	onSetAlert,
	onRequestCreated,
}) => {
	const [operationsModalOpen, setOperationsModalOpen] = useState(false);
	const [emissionsModalOpen, setEmissionsModalOpen] = useState(false);
	const [refundModalOpen, setRefundModalOpen] = useState(false);
	const [creditCardModalOpen, setCreditCardModalOpen] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [showOptions, setShowOptions] = useState(false);
	const [existingRequests, setExistingRequests] = useState<IOperationalRequestResponse[]>([]);

	// Cargar solicitudes existentes
	useEffect(() => {
		const fetchRequests = async () => {
			try {
				const response = await getOperationalRequests(saleOrderId);
				setExistingRequests(response.data);
			} catch (error) {
				console.error('Error fetching operational requests:', error);
			}
		};

		fetchRequests();
	}, [saleOrderId, refreshKey]);

	const handleSuccess = () => {
		// El mensaje de éxito ya se muestra en el modal
		// Refrescar la lista de solicitudes
		setRefreshKey((prev) => prev + 1);
		setShowOptions(false); // Cerrar las opciones después de crear
		if (onRequestCreated) {
			onRequestCreated();
		}
	};

	const hasRequests = existingRequests.length > 0;

	return (
		<>
			<Card elevation={4} sx={{ mb: 2 }}>
				<CardContent>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Typography variant="h6">🤝 Solicitudes operativas</Typography>
						<IconButton
							color="primary"
							onClick={() => setShowOptions(!showOptions)}
							sx={{
								border: '1px solid',
								borderColor: 'primary.main',
								'&:hover': {
									backgroundColor: 'action.hover',
								},
							}}
						>
							<AddIcon />
						</IconButton>
					</Box>

					{!showOptions && !hasRequests && (
						<Typography variant="body2" color="text.secondary">
							Cree solicitudes operativas directamente desde la orden. Estas se crearán como casos en el CRM y quedarán
							asociadas a esta orden.
						</Typography>
					)}

					<Collapse in={showOptions}>
						<Box sx={{ mt: 2, mb: hasRequests ? 3 : 0 }}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6} md={3}>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<OperationsIcon />}
										onClick={() => {
											setOperationsModalOpen(true);
											setShowOptions(false);
										}}
										sx={{ py: 2, height: '100%', flexDirection: 'column', gap: 1 }}
									>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Operaciones de servicios
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Aviso de llegada, modificación, cancelación
										</Typography>
									</Button>
								</Grid>

								<Grid item xs={12} sm={6} md={3}>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<EmissionsIcon />}
										onClick={() => {
											setEmissionsModalOpen(true);
											setShowOptions(false);
										}}
										sx={{ py: 2, height: '100%', flexDirection: 'column', gap: 1 }}
									>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Emisiones
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Voidear, apertura, servicios especiales
										</Typography>
									</Button>
								</Grid>

								<Grid item xs={12} sm={6} md={3}>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<RefundsIcon />}
										onClick={() => {
											setRefundModalOpen(true);
											setShowOptions(false);
										}}
										sx={{ py: 2, height: '100%', flexDirection: 'column', gap: 1 }}
									>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Devoluciones
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Errores, aéreos, alojamiento, otros
										</Typography>
									</Button>
								</Grid>

								<Grid item xs={12} sm={6} md={3}>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<CreditCardIcon />}
										onClick={() => {
											setCreditCardModalOpen(true);
											setShowOptions(false);
										}}
										sx={{ py: 2, height: '100%', flexDirection: 'column', gap: 1 }}
									>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Consulta sobre cobros
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Contracargos, duplicados, no cobros
										</Typography>
									</Button>
								</Grid>
							</Grid>
						</Box>
					</Collapse>

					{hasRequests && (
						<Box sx={{ mt: 3 }}>
							<OperationalRequestsList key={refreshKey} saleOrderId={saleOrderId} />
						</Box>
					)}
				</CardContent>
			</Card>

			<>
				<OperationsServiceRequestModal
					open={operationsModalOpen}
					onClose={() => setOperationsModalOpen(false)}
					services={services}
					saleOrderId={saleOrderId}
					onSuccess={handleSuccess}
					onSetAlert={onSetAlert}
				/>

				<EmissionsRequestModal
					open={emissionsModalOpen}
					onClose={() => setEmissionsModalOpen(false)}
					services={services}
					passengers={passengers}
					saleOrderId={saleOrderId}
					onSuccess={handleSuccess}
					onSetAlert={onSetAlert}
				/>

				<RefundRequestModal
					open={refundModalOpen}
					onClose={() => setRefundModalOpen(false)}
					services={services}
					passengers={passengers}
					saleOrderId={saleOrderId}
					onSuccess={handleSuccess}
					onSetAlert={onSetAlert}
				/>

				<CreditCardInquiryModal
					open={creditCardModalOpen}
					onClose={() => setCreditCardModalOpen(false)}
					transactions={transactions}
					saleOrderId={saleOrderId}
					onSuccess={handleSuccess}
					onSetAlert={onSetAlert}
				/>
			</>
		</>
	);
};
